/*
 * Caseta care întreabă — §9 din contract.
 *
 * DESENEAZĂ DOAR ÎNTREBAREA. Rezultatele le desenează ecranul, în coloana lui
 * din dreapta, fiindcă acolo stau lângă formularul de cerere din stânga. Caseta
 * le urcă prin `onSearch` și nu știe nimic despre unde ajung — ea răspunde la o
 * întrebare, iar ce se face cu răspunsul e treaba ecranului.
 *
 * Omul scrie ce vrea. Se caută în două locuri deodată și i se dau DOUĂ LISTE
 * SEPARATE, etichetate.
 *
 * ---------------------------------------------------------------------------
 * DE CE DOUĂ GRUPE ȘI NU UNA ORDONATĂ DUPĂ SCOR
 * ---------------------------------------------------------------------------
 *
 * Catalogul răspunde la „vreau să cer ceva". Articolele răspund la „ceva nu
 * merge / nu știu cum". Puse laolaltă, omul nu mai știe ce e o comandă și ce e
 * o instrucțiune — apasă pe un articol crezând că a cerut un laptop.
 *
 * Articolele primele: dacă se poate rezolva singur, aia e cea mai bună variantă
 * pentru amândoi.
 *
 * ---------------------------------------------------------------------------
 * IEȘIREA CĂTRE TICHET RĂMÂNE MEREU DEDESUBT
 * ---------------------------------------------------------------------------
 *
 * Caseta asta stă DEASUPRA formularului de tichet, nu în locul lui, și nu-l
 * ascunde niciodată. Un ecran care ascunde ieșirea până când eșuezi în căutare
 * e un ecran care te ține ostatic.
 */

import { useEffect, useState } from 'react';
import * as shop from '../api/shop';
import type { CatalogItem, HelpArticleSummary, SearchResponse } from '../api/types';
import { getLang, t } from '../lib/i18n';
import { useAsync } from '../lib/useAsync';

/** Ce știe caseta, și ecranul de sub ea are nevoie să știe. */
export interface SearchState {
  /**
   * Id-ul căutării curente.
   *
   * Când omul deschide totuși un tichet după ce a citit, tichetul poartă ce a
   * citit. Fără asta nu se află niciodată care articole nu funcționează, iar un
   * articol prost trăiește la nesfârșit.
   */
  searchId: number;
  /** Ce a scris omul, așa cum a scris. Devine subiectul cererii. */
  query: string;
  /** S-a căutat și s-a primit răspuns? Fals cât timp caseta e goală sau caută. */
  searched: boolean;
  /** S-a găsit ceva — articol sau serviciu? */
  found: boolean;
  /** Articolele găsite. Caseta nu le mai desenează ea; le desenează ecranul. */
  articles: HelpArticleSummary[];
  /** Serviciile din catalog care se potrivesc. */
  services: CatalogItem[];
}

/*
 * O singura lista goala pentru toate randarile.
 *
 * `[]` scris in corpul componentei e un obiect nou de fiecare data, iar un
 * obiect nou intr-o dependinta de efect e o bucla. Vezi nota de la `useEffect`.
 */
const EMPTY: never[] = [];

interface Props {
  /**
   * Starea căutării, urcată către ecran.
   *
   * Ecranul de sub caseta asta decide pe baza ei DACĂ arată formularul de
   * cerere, si cu ce completat. Caseta nu stie nimic despre formular, si nici
   * n-ar trebui: ea raspunde la o intrebare, iar ce se face cu raspunsul e
   * treaba ecranului.
   */
  onSearch?: (state: SearchState) => void;
  /**
   * Id-ul titlului care pune întrebarea, când ecranul o pune el.
   *
   * Altfel caseta și-o pune singură, ca etichetă. Două întrebări una sub alta —
   * un titlu și o etichetă care spun același lucru — se citesc ca o scăpare,
   * iar ecranul are nevoie oricum de un `h1`.
   */
  labelledBy?: string;
  /**
   * Ce sta deja scris in caseta cand se deschide.
   *
   * Vine de la un articol citit pana la capat fara folos: omul a descris deja
   * problema o data - alegand articolul - si n-are de ce s-o scrie a doua oara.
   * Pleaca direct, fara pauza de tastare: nu e nimic tastat pe jumatate.
   */
  initialQuery?: string;
}

export function SelfServeSearch({ onSearch, labelledBy, initialQuery = '' }: Props) {
  const [typed, setTyped] = useState(initialQuery);
  const [query, setQuery] = useState(initialQuery.trim());

  /*
   * Căutarea pleacă după o pauză în tastare — același motiv ca la catalog: fără
   * pauză se trimite o cerere pe literă, iar răspunsurile se întorc în altă
   * ordine decât au plecat.
   *
   * 400ms, nu 300 ca la catalog: acolo omul filtrează o grilă pe care o vede
   * deja și vrea reacție imediată; aici scrie o propoziție întreagă, iar o
   * căutare pe fiecare cuvânt scris pe jumătate n-ar găsi nimic și ar clipi.
   */
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(typed.trim()), 400);
    return () => window.clearTimeout(timer);
  }, [typed]);

  const found = useAsync<SearchResponse | null>(
    (signal) => (query === '' ? Promise.resolve(null) : shop.search(query, getLang(), { signal })),
    [query],
  );

  const data = found.status === 'ready' ? found.data : null;
  /*
   * O căutare căzută se numără ca „s-a căutat și n-a ieșit nimic".
   *
   * Altfel, când api-ul nu răspunde, formularul de cerere n-ar mai apărea
   * niciodată și omul ar rămâne cu o casetă mută și fără nicio ieșire — exact
   * situația în care are cel mai mult nevoie de una.
   */
  const searched = query !== '' && (found.status === 'ready' || found.status === 'error');

  /*
   * `data` in dependinte, NU listele scoase din el.
   *
   * O BUCLA DE RANDARE, si una tacuta. `data?.articles ?? []` face un array NOU
   * la fiecare randare cat timp `data` e nul - adica pana cand omul scrie ceva.
   * Pus in dependinte, efectul se aprindea la fiecare randare, urca starea in
   * ecran, ecranul redesena, iar caseta primea alt `[]`. La masuratoare: 3,2
   * secunde de JavaScript in 4 secunde de repaus, ~80% dintr-un procesor.
   *
   * Nu se vedea NIMIC pe ecran - randarile scoteau acelasi DOM - si nici React
   * nu se plangea, fiindca actualizarile nu sunt imbricate sincron, ci una pe
   * fiecare comitere. Se vede numai la contorul de procesor al browserului.
   *
   * `data` e stabil: ori acelasi obiect intors de api, ori `null`. `EMPTY` e o
   * singura lista goala, impartita de toate randarile.
   */
  useEffect(() => {
    onSearch?.({
      searchId: data?.search_id ?? 0,
      query,
      searched,
      found: (data?.articles?.length ?? 0) > 0 || (data?.services?.length ?? 0) > 0,
      articles: data?.articles ?? EMPTY,
      services: data?.services ?? EMPTY,
    });
  }, [onSearch, data, query, searched]);

  return (
    <section className="selfserve" aria-labelledby={labelledBy ?? 'selfserve-label'}>
      <div className="field">
        {labelledBy ? null : (
          /* Întrebarea e pusă unui om, nu unui formular — deci arată ca o
             întrebare, nu ca eticheta măruntă a unui câmp. */
          <label className="selfserve__ask" id="selfserve-label" htmlFor="selfserve-q">
            {t('How can we help you today?')}
          </label>
        )}
        <input
          id="selfserve-q"
          className="field__control selfserve__input"
          type="search"
          autoComplete="off"
          aria-labelledby={labelledBy}
          placeholder={t('Printer prints nothing, my password expired, I need a laptop…')}
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
        />
        <p className="field__hint">
          {t('We search all the help articles and the catalog automatically. If nothing matches, we open a support request.')}
        </p>
      </div>

      {/*
        * Căderea căutării NU e o eroare de arătat cu chenar roșu.
        *
        * Formularul de tichet e chiar dedesubt și merge mai departe; omul nu e
        * blocat, doar nu i-am putut oferi scurtătura. Un ErrorState aici ar fi
        * arătat ca și cum ecranul s-ar fi stricat, exact în clipa în care omul
        * are deja o problemă.
        */}
      {found.status === 'error' ? (
        <p className="selfserve__note">{t('The search is not answering right now.')}</p>
      ) : null}

      {query !== '' && found.status === 'loading' ? (
        <p className="selfserve__note">{t('Searching…')}</p>
      ) : null}

    </section>
  );
}
